/**
 * The three server calls the wizard makes, and the state that is *only* an
 * answer from the server.
 *
 * Split out of `LetterGenerator` under the 400-line rule in
 * `docs/FILE_SIZE_POLICY.md`. The seam is not arbitrary: everything here is
 * downstream of the network and nothing here is a form value. The page keeps
 * what the CPA typed; this hook keeps what the server said about it, which is
 * why the invalidation effect below belongs on this side of the line.
 *
 * Refusals deliberately do not go through `useApi`. That hook flattens an error
 * to a string via `formatApiError`, which would reduce a structured refusal to
 * "422 - Unprocessable Entity" and throw away the reason the letter cannot be
 * written. Each call catches the raw error, runs `parseAttestRefusal`, and falls
 * back to `formatApiError` only when the failure was not an attest refusal at
 * all (network, 401, 500).
 */
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import {
  classifyRequest,
  downloadLetterDocx,
  hydrateBlobError,
  parseAttestRefusal,
  previewLetter,
  routingRefusal,
  type AttestRefusal,
  type LetterArtifact,
  type LetterTypeSummary,
  type RoutingDecision,
} from '@/components/attest/api'
import {
  AT_C_215_AUP,
  splitRequestedItems,
  type ClassifyFormState,
} from '@/components/attest/classifyQuestions'
import {
  buildLetterRequest,
  type IssuerFormState,
  type LetterFormState,
} from '@/components/attest/formState'
import { formatApiError } from '@/utils/httpErrors'

/** Everything the three calls read. All of it is form state the page owns. */
export interface LetterCallInputs {
  /** The type as the chosen basis rewrites it — `null` until one is picked. */
  letterType: LetterTypeSummary | null
  selectedTypeId: string
  selectedBasis: string
  classify: ClassifyFormState
  form: LetterFormState
  issuer: IssuerFormState
}

export function useLetterCalls({
  letterType,
  selectedTypeId,
  selectedBasis,
  classify,
  form,
  issuer,
}: LetterCallInputs) {
  const [decision, setDecision] = useState<RoutingDecision | null>(null)
  const [classifyRefusal, setClassifyRefusal] = useState<AttestRefusal | null>(null)
  const [artifact, setArtifact] = useState<LetterArtifact | null>(null)
  const [letterRefusal, setLetterRefusal] = useState<AttestRefusal | null>(null)
  const [confirmed, setConfirmed] = useState<Record<number, boolean>>({})

  const [classifying, setClassifying] = useState(false)
  const [building, setBuilding] = useState(false)
  const [downloading, setDownloading] = useState(false)

  /**
   * ATTEST-INDEPENDENCE-MATRIX-1 (F) — a draft and a refusal are both statements
   * about the inputs that produced them, so neither outlives an edit to those
   * inputs.
   *
   * The stale refusal is the worse half. Leaving it on screen after the CPA has
   * ticked the box it complained about tells them their fix didn't work, which
   * is a lie in the direction that makes people stop trusting the gate. The
   * stale draft is a subtler version of the same problem: a Download button
   * still lit next to changed inputs implies the file matches them.
   *
   * `confirmed` goes too — those are acknowledgements of *this* draft's
   * disclaimers, and carrying them onto the next one would be a signature
   * collected for a document that no longer exists.
   */
  useEffect(() => {
    setArtifact(null)
    setLetterRefusal(null)
    setConfirmed({})
  }, [selectedTypeId, selectedBasis, form, issuer])

  /** Attest refusal → panel; anything else → the generic error path. */
  const handleFailure = useCallback((err: unknown, set: (r: AttestRefusal | null) => void) => {
    const refusal = parseAttestRefusal(err)
    if (refusal) {
      set(refusal)
      return
    }
    set(null)
    toast.error(formatApiError(err))
  }, [])

  const onClassify = useCallback(async () => {
    setClassifying(true)
    setClassifyRefusal(null)
    setDecision(null)
    try {
      const result = await classifyRequest({
        requested_items: splitRequestedItems(classify.requestedItemsText),
        // Derived from the basis, never asked (ATTEST-BASIS-UNIFY-1). With a
        // type named the server ignores this field and reads the basis itself,
        // so sending anything else would only put a second, contradictable
        // answer on the wire. It still carries the typeless case, where there
        // is no basis for either side to read.
        requires_procedures_beyond_own_records: letterType?.engagement_type === AT_C_215_AUP,
        procedures_agreed_by_specified_parties: classify.proceduresAgreedBySpecifiedParties,
        issuer_is_attest_eligible: classify.issuerIsAttestEligible,
        requested_assurance_level: classify.requestedAssuranceLevel || null,
        letter_type_id: selectedTypeId || null,
        engagement_basis: selectedBasis || null,
      })
      setDecision(result)
      // A 200 that refuses is still a refusal — render it like one.
      setClassifyRefusal(routingRefusal(result))
    } catch (err) {
      handleFailure(err, setClassifyRefusal)
    } finally {
      setClassifying(false)
    }
  }, [classify, letterType, selectedTypeId, selectedBasis, handleFailure])

  const onPreview = useCallback(async () => {
    if (!letterType) return
    setBuilding(true)
    setLetterRefusal(null)
    try {
      const result = await previewLetter(
        buildLetterRequest(letterType.type_id, form, issuer, selectedBasis),
      )
      setArtifact(result)
      setConfirmed({})
    } catch (err) {
      setArtifact(null)
      handleFailure(err, setLetterRefusal)
    } finally {
      setBuilding(false)
    }
  }, [letterType, form, issuer, selectedBasis, handleFailure])

  const onDownload = useCallback(async () => {
    if (!letterType) return
    setDownloading(true)
    setLetterRefusal(null)
    try {
      const name = `${letterType.type_id}_${form.subjectEntity || 'letter'}.docx`
        .replace(/[^\w.-]+/g, '_')
      await downloadLetterDocx(
        buildLetterRequest(letterType.type_id, form, issuer, selectedBasis),
        name,
      )
    } catch (err) {
      // The refusal arrived as a Blob because the request asked for one.
      handleFailure(await hydrateBlobError(err), setLetterRefusal)
    } finally {
      setDownloading(false)
    }
  }, [letterType, form, issuer, selectedBasis, handleFailure])

  return {
    decision,
    classifyRefusal,
    artifact,
    letterRefusal,
    confirmed,
    setConfirmed,
    classifying,
    building,
    downloading,
    onClassify,
    onPreview,
    onDownload,
  }
}
